import { useEffect } from "react";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  SITE_NAME,
  TWITTER_HANDLE,
  absoluteUrl,
} from "@/lib/seo/config";

/**
 * Per-page head tags. Render exactly ONE <Seo /> per page (inside the page component).
 *
 * Uses React 19 native document metadata: <title>, <meta>, <link> and <script> rendered
 * here are hoisted into <head> automatically and removed when the page unmounts.
 *
 * Props:
 *  - title        string  (<= ~60 chars recommended)
 *  - description  string  (<= ~155 chars recommended)
 *  - path         string  in-app path used for the canonical URL, e.g. "/industries/health"
 *  - image        string  absolute URL for og:image / twitter:image
 *  - type         "website" | "article" (og:type)
 *  - noindex      boolean  true for thank-you / utility pages
 *  - jsonLd       object | object[]  structured data (see src/lib/seo/schema.js)
 */
export function Seo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  const url = absoluteUrl(path);
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  // Remove the static no-JS fallbacks from public/index.html so the page has a single title and description.
  useEffect(() => {
    document.head.querySelectorAll("[data-seo-default]").forEach((node) => node.remove());
  }, []);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex ? null : <link rel="canonical" href={url} />}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {TWITTER_HANDLE ? <meta name="twitter:site" content={TWITTER_HANDLE} /> : null}

      {schemas.map((schema, index) => (
        <script key={`${schema["@type"] ?? "schema"}-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </>
  );
}
